import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

/**
 * Find-in-pane, with the same shape on every review pane.
 *
 * It matches rather than filters, which is what the payroll grid's search does
 * and what these panes need: the roster's counts, the access pane's sheet
 * totals and the change stepper's indices all describe the whole list, and a
 * filter would leave them describing something that is no longer on screen.
 *
 * Callers give it the searchable text of each row, in the order the rows are
 * rendered, and get back which rows match and which one is current.
 */
export const usePaneSearch = (
  /** One entry per row, in render order. Each is the row's searchable text. */
  haystack: { id: string; text: string }[],
  /**
   * Anything inside the pane. The find shortcut only answers while that node
   * is on screen, which is how four mounted panes share one keystroke.
   */
  root?: RefObject<HTMLElement | null>,
) => {
  const [open, setOpen] = useState(false);
  const [query, updateQuery] = useState("");
  const [step, setStep] = useState(0);
  // Bumped on every open so the input refocuses even when it was already open.
  const [focusRequest, setFocusRequest] = useState(0);

  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle
        ? haystack
            .filter((row) => row.text.toLowerCase().includes(needle))
            .map((row) => row.id)
        : [],
    [haystack, needle],
  );

  // Derived, not stored. The rows can change under the reader — a turn can
  // rewrite the roster while a search is open — and a kept index would point
  // past the end of a shorter list.
  const at = matches.length
    ? ((step % matches.length) + matches.length) % matches.length
    : 0;
  const activeId = matches[at] ?? null;

  // Reset the cursor with the input change, so no render selects an old match.
  const setQuery = useCallback((value: string) => {
    updateQuery(value);
    setStep(0);
  }, []);

  const goTo = useCallback(
    (by: number) =>
      setStep((current) => {
        if (!matches.length) return current;
        const next =
          (((current + by) % matches.length) + matches.length) % matches.length;
        return next;
      }),
    [matches.length],
  );

  // How many find bars this hook is driving. Counted rather than a boolean, so
  // a pane that renders its control twice cannot turn the other one off.
  const mounted = useRef(0);
  const attach = useCallback(() => {
    mounted.current += 1;
    return () => {
      mounted.current -= 1;
    };
  }, []);

  const show = useCallback(() => {
    setOpen(true);
    setFocusRequest((version) => version + 1);
  }, []);

  // Find means find. Taking the browser's own ⌘F is a thing to do carefully,
  // and the test here is what makes it safe: every review pane stays mounted so
  // an unsent draft survives a tab change, so all four hooks hear the keystroke
  // and only the one whose node is actually laid out may answer. A hidden
  // pane's node has no `offsetParent`, and a pane still showing its skeleton
  // has no node at all, so neither can steal the key from the browser.
  //
  // The control saying it is on screen is the other half of that test. A pane
  // can be laid out and still be showing its empty state, where there is no
  // find bar to open: the shortcut was taken from the browser and nothing
  // appeared. The control registers while it is mounted, so the hook answers
  // only for a find bar that exists.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "f" || !(event.metaKey || event.ctrlKey)) return;
      if (!mounted.current) return;
      const node = root?.current;
      if (!node || node.offsetParent === null) return;
      event.preventDefault();
      show();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [root, show]);

  const hide = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, [setQuery]);

  return {
    open,
    query,
    setQuery,
    show,
    hide,
    focusRequest,
    /** Row ids that match, in render order. */
    matches,
    /** The one the reader is on, or null. */
    activeId,
    at,
    goTo,
    /** Called by the find bar while it is mounted, so ⌘F knows it is there. */
    attach,
  };
};

export type PaneSearch = ReturnType<typeof usePaneSearch>;
